import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  InMemoryCache,
  Observable,
  StoreObject,
  createHttpLink,
  from,
  split,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { PropsWithChildren, useCallback, useEffect, useMemo } from 'react';

import { sortFragmentDefinitionsLink } from '@grow-therapy-team/seedling-components';

import { useGetAccessTokenSilentlyCallback, useHandleNeedsReLoginCallback } from '../auth';
import { authedAtTimestampAtom, needsReLoginAtom } from '../auth/state';
import { GRAPHQL_PATH, getAppConfig } from '../config';
import { registerAppFragments } from './fragments';
import { Server } from './types';
import { MISSING_AUTH_HEADER_ERROR_MESSAGE } from './utils';

const appConfig = getAppConfig();

const authLink = new ApolloLink((operation, forward) => {
  const { skipAuth, headers } = operation.getContext();
  if (!skipAuth && !headers?.Authorization) {
    return new Observable((observer) => {
      observer.error(new Error(`${MISSING_AUTH_HEADER_ERROR_MESSAGE} for operation: ${operation.operationName}`));
    });
  }

  return forward(operation);
});

const telehealthHttpLink = createHttpLink({
  uri: GRAPHQL_PATH,
});

const monolithHttpLink = createHttpLink({
  uri: appConfig.monolithBackendOrigin + appConfig.monolithGqlPath,
});

const httpLink = split(
  (operation) => {
    return operation.getContext().server === Server.MONOLITH;
  },
  monolithHttpLink,
  telehealthHttpLink,
);

function dataIdFromObject(result: StoreObject) {
  switch (result.__typename) {
    case 'RecurringAppointmentGroupingObject':
      return `${result.__typename}${result.recurringAppointmentGroupingId}`;
    case 'ProviderObject':
      return `${result.__typename}${result.shortId ?? result.providerShortId ?? result.id}`;
    case 'ClientRequiresAuthedIntroRoomObject':
    case 'ClientUserObject':
      return `${result.__typename}${result.clientUserShortId}`;
    case 'MeasureBundleObject':
      if (result.measureBundleShortId) {
        return `${result.__typename}${result.measureBundleShortId}`;
      }
      return undefined;
    case 'MeasureBundleItemObject':
      if (result.measureBundleItemId) {
        return `${result.__typename}${result.measureBundleItemId}`;
      }
      return undefined;
    case 'MeasureSchemaObject':
      if (result.measureSchemaShortId) {
        return `${result.__typename}${result.measureSchemaShortId}`;
      }
      return undefined;
    case 'FormObject':
      if (result.formShortId) {
        return `${result.__typename}${result.formShortId}`;
      }
      return undefined;
    case 'InsuranceCoverageObject':
      if (result.uuid != null) {
        return `${result.__typename}${result.uuid}`;
      }
      return undefined;
    case 'PublicPatientObject':
      if (result.customerId) {
        return `${result.__typename}${result.customerId}`;
      }
      return undefined;
    default:
      if (result.shortId) {
        return `${result.__typename}${result.shortId}`;
      }
      if (result.id) {
        return `${result.__typename}${result.id}`;
      }
      return undefined;
  }
}

/**
 * Wraps the given children with an ApolloProvider that attaches an Auth0 JWT to
 * GQL request headers, if a token is available.
 *
 * In the event that the refresh token is invalid i.e. the session timed out,
 * then the user will be logged out and returned to the login screen.
 */
export function AuthedApolloProvider({ children }: { children: React.ReactNode }) {
  const getAuthedAtTimestamp = useAtomCallback(useCallback((get) => get(authedAtTimestampAtom), []));
  const getAccessToken = useGetAccessTokenSilentlyCallback();

  const withAccessTokenLink = useMemo(
    () =>
      setContext(async (_, ctx) => {
        if (ctx.skipAuth) return ctx;

        const [token] = await getAccessToken({
          showToast: getAuthedAtTimestamp() !== null,
        });
        if (!token) return ctx;
        return {
          ...ctx,
          headers: {
            ...ctx.headers,
            Authorization: `Bearer ${token}`,
          },
        };
      }),
    [getAccessToken, getAuthedAtTimestamp],
  );

  const apolloClient = useMemo(
    () =>
      new ApolloClient({
        link: from([sortFragmentDefinitionsLink, withAccessTokenLink, authLink, httpLink]),
        cache: new InMemoryCache({
          fragments: registerAppFragments(),
          dataIdFromObject,
        }),
      }),
    [withAccessTokenLink],
  );

  return (
    <ApolloProvider client={apolloClient}>
      <NeedReLoginHandler>{children}</NeedReLoginHandler>
    </ApolloProvider>
  );
}

/**
 * A tiny component that listens for changes to the `needsReLoginAtom` atom and
 * invokes the `handleNeedsReLogin` callback when it changes to true, to trigger
 * any side-effects that should occur when the user's session has expired. It
 * does not render anything itself; however, it needs to be a _child_ of the
 * ApolloProvider so that side-effects that rely on the context of the Apollo
 * client are able to run.
 */
function NeedReLoginHandler({ children }: PropsWithChildren) {
  const needsReLogin = useAtomValue(needsReLoginAtom);
  const handleNeedsReLogin = useHandleNeedsReLoginCallback();
  useEffect(() => {
    if (needsReLogin) handleNeedsReLogin();
  }, [handleNeedsReLogin, needsReLogin]);

  return <>{children}</>;
}
